import React, { useEffect, useState } from "react";
import { Col, Row, Spinner } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import {
  SALES_ORDER_CATEGORY,
} from "Constants/StatusConstant";
// Import child components from Common
import ActivityFeed from "Common/CommonFollowUpForEnquirySalesOrder/ActivityFeed";
import AssignedMembers from "Common/CommonFollowUpForEnquirySalesOrder/AssignedMembers";
import ScheduleFollowUp from "Common/CommonFollowUpForEnquirySalesOrder/ScheduleFollowUp";

import {
  createFollowUpByType,
  fetchFollowUpsByType,
} from "slices/FollowUp/thunk";

interface AssignedMember {
  id: string; // ID of the assignment record
  user: string; // User ID
  username: string; // Display Name
  initials: string; // Calculated initials
  bg: string; // Display color
  color: string; // Display text color
  isCreatedBy?: boolean; // Flag to indicate if this is the "created by" user
}

interface Activity {
  id: string;
  user: string;
  date: string;
  text: string;
  initials: string;
  bg: string;
  color: string;
  follow_up_type_name: string;
  source_entity_type?: string;
  source_module_label?: string;
}

const HARDCODED_FOLLOW_UP_TYPES = [
  { id: "call", name: "Call" },
  { id: "message", name: "Message" },
  { id: "email", name: "Email" },
  { id: "meeting", name: "Meeting" },
  { id: "onsitevisit", name: "On Site Visit" },
];

const AVATAR_COLORS = [
  { bg: "#FFCDD2", color: "#B71C1C" }, // red
  { bg: "#C8E6C9", color: "#1B5E20" }, // green
  { bg: "#BBDEFB", color: "#0D47A1" }, // blue
  { bg: "#FFF9C4", color: "#F57F17" }, // yellow
  { bg: "#D1C4E9", color: "#4A148C" }, // purple
  { bg: "#FFE0B2", color: "#E65100" }, // orange
  { bg: "#B2EBF2", color: "#006064" }, // cyan
  { bg: "#F8BBD0", color: "#880E4F" }, // pink
  { bg: "#DCEDC8", color: "#33691E" }, // lime
  { bg: "#CFD8DC", color: "#263238" }, // blue-grey
];

// --- Date helpers (required flow: local UI ↔ UTC backend) ---
const pad2 = (n: number) => String(n).padStart(2, "0");

// IMPORTANT: `ActivityFeed` parses `activity.date` with `new Date(...)`.
// To avoid double-parsing / timezone drift, always keep `activity.date` as ISO (UTC) from API.

// Convert `datetime-local` (local) → UTC ISO for backend storage.
const localDateTimeToUtcIso = (localDateTime: string): string => {
  if (!localDateTime) return "";
  const d = new Date(localDateTime); // interpreted as local time
  return Number.isNaN(d.getTime()) ? localDateTime : d.toISOString();
};

// Convert `date` (local day) → UTC ISO (local midnight converted to UTC).
const localDateToUtcIso = (localDate: string): string => {
  if (!localDate) return "";
  const [y, m, day] = localDate.split("-").map(Number);
  if (!y || !m || !day) return localDate;
  const d = new Date(y, m - 1, day, 0, 0, 0);
  return Number.isNaN(d.getTime()) ? localDate : d.toISOString();
};

// In Retention/PBG follow-ups, the API may include an initial record with no `follow_up_date`.
// We must hide ONLY the first such record from the Comments/Activity feed.
const excludeFirstNoDateFollowUp = (items: any[]): any[] => {
  const idx = items.findIndex((x) => !x?.follow_up_date);
  if (idx === -1) return items;
  return [...items.slice(0, idx), ...items.slice(idx + 1)];
};

const SOURCE_ENTITY_ID_MAP: Record<string, string> = {
  QUOTATION_PRODUCT: "quotation_product_id",
  QUOTATION_SERVICE: "quotation_service_id",

  ENQUIRY_PRODUCT: "enquiry_product_id",
  ENQUIRY_SERVICE: "enquiry_service_id",

  SALES_ORDER_PRODUCT: "sales_order_product_id",
  SALES_ORDER_SERVICE: "sales_order_service_id",
};

const SOURCE_ENTITY_TYPE_TO_MODULE: Record<string, string> = {
  ENQUIRY_PRODUCT: "Enquiry",
  ENQUIRY_SERVICE: "Enquiry",
  QUOTATION_PRODUCT: "Quotation",
  QUOTATION_SERVICE: "Quotation",
  SALES_ORDER_PRODUCT: "Sales Order",
  SALES_ORDER_SERVICE: "Sales Order",
};

const getSourceModuleLabel = (item: any): string => {
  const sourceEntityType = String(item?.source_entity_type || "").toUpperCase();
  const followUpCategory = String(item?.follow_up_category || "").toUpperCase();

  // For sales-order source, backend category decides module:
  // - Other => Sales Order
  // - Any other non-empty category => Payment Management
  if (
    (sourceEntityType === "SALES_ORDER_PRODUCT" ||
      sourceEntityType === "SALES_ORDER_SERVICE") &&
    followUpCategory
  ) {
    return followUpCategory === "OTHER" ? "Sales Order" : "Payment Management";
  }

  return SOURCE_ENTITY_TYPE_TO_MODULE[sourceEntityType] || "Other";
};

interface FollowupModelProps {
  data: any;
  activeType: string;
  category?: string;
  onClose?: () => void;
  onSuccess?: () => void;
  showAssignedMembers?: boolean;
}

const getCurrentDateTime = () => {
  const now = new Date();
  const offset = now.getTimezoneOffset();
  const local = new Date(now.getTime() - offset * 60 * 1000);
  return local.toISOString().slice(0, 16);
};

const FollowupModel: React.FC<FollowupModelProps> = ({
  data,
  activeType,
  category,
  onClose,
  onSuccess,
  showAssignedMembers = true,
}) => {
  const dispatch = useDispatch<any>();
  const [assignedMembers, setAssignedMembers] = useState<AssignedMember[]>([]);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [followUpData, setFollowUpData] = useState({
    followUpType: "",
    dateTime: getCurrentDateTime(),
    nextFollowUpDate: "",
    attachmentFiles: [] as File[],
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editorContent, setEditorContent] = useState("");
  const [editorResetKey, setEditorResetKey] = useState(0);

  // Enforce "Follow up Date" >= last follow-up date (if any).
  // `activities[].date` is stored as UTC ISO from API.
  const lastFollowUpUtcIso: string | undefined = React.useMemo(() => {
    const maxTime = activities.reduce<number>((acc, a) => {
      const t = a?.date ? Date.parse(a.date) : NaN;
      return Number.isFinite(t) ? Math.max(acc, t) : acc;
    }, -Infinity);

    return Number.isFinite(maxTime) ? new Date(maxTime).toISOString() : undefined;
  }, [activities]);

  const minFollowUpDateTimeLocal: string | undefined = React.useMemo(() => {
    if (!lastFollowUpUtcIso) return undefined;
    const d = new Date(lastFollowUpUtcIso); // converts to local time getters
    if (Number.isNaN(d.getTime())) return undefined;

    // datetime-local format: YYYY-MM-DDTHH:mm (local)
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(
      d.getHours()
    )}:${pad2(d.getMinutes())}`;
  }, [lastFollowUpUtcIso]);

  // Map Activites
  const mapFollowUpToActivity = (item: any) => {
    const firstName = item?.created_by_user?.first_name || "";
    const lastName = item?.created_by_user?.last_name || "";
    const fullName = `${firstName} ${lastName}`.trim() || "Unknown User";
    const initials = `${firstName[0] || ""}${lastName[0] || ""}`.toUpperCase();

    // 🔹 Deterministic color selection
    const colorSeed = (firstName + lastName).charCodeAt(0) || 0;
    const colorIndex = colorSeed % AVATAR_COLORS.length;
    const { bg, color } = AVATAR_COLORS[colorIndex];

    return {
      id: item.follow_up_id,
      user: fullName,
      date: item.created_at || item.follow_up_date || "",
      text: item.follow_up_remarks || "",
      initials,
      bg,
      color,
      follow_up_type_name: item.follow_up_type
        ? String(item.follow_up_type).charAt(0).toUpperCase() +
          String(item.follow_up_type).slice(1)
        : "Follow-up",
      source_entity_type: item?.source_entity_type || "",
      source_module_label: getSourceModuleLabel(item),
    };
  };

  // Map Members
  const mapAssignedMember = (item: any): AssignedMember => {
    console.log("ITEM NAME", item);
    const firstName = item.first_name || "";
    const lastName = item.last_name || "";
    const fullName =
      `${firstName} ${lastName}`.trim() || item.username || "Unknown User";
    const initials = `${firstName[0] || ""}${lastName[0] || ""}`.toUpperCase();
    // deterministic color (same user → same color)
    const colorIndex = initials.charCodeAt(0) % AVATAR_COLORS.length || 0;

    return {
      id: item.assigned_member_id,
      user: item.user_id,
      username: fullName,
      initials,
      bg: AVATAR_COLORS[colorIndex].bg,
      color: AVATAR_COLORS[colorIndex].color,
    };
  };

  // --- Handlers --- Date and time
  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value, files } = e.target as HTMLInputElement;
    if (name === "attachments" && files) {
      setFollowUpData((prev) => ({
        ...prev,
        attachmentFiles: Array.from(files),
      }));
    } else {
      setFollowUpData((prev) => ({ ...prev, [name]: value }));
    }
  };

  //   Submit Follow Up
  const handleSubmitFollowUp = async (e: React.FormEvent) => {
    e.preventDefault();

    // 🔹 Validations
    if (!followUpData.followUpType) {
      toast.error("Please select follow-up type");
      return;
    }

    if (!followUpData.dateTime) {
      toast.error("Please select follow-up date");
      return;
    }

    // Prevent scheduling in the past relative to last follow-up date.
    if (lastFollowUpUtcIso) {
      const selectedTime = new Date(followUpData.dateTime).getTime(); // local input
      const lastTime = new Date(lastFollowUpUtcIso).getTime(); // UTC ISO → absolute time
      if (Number.isFinite(selectedTime) && Number.isFinite(lastTime) && selectedTime < lastTime) {
        toast.error("Follow up date cannot be earlier than the last follow up date");
        return;
      }
    }

    // Treat empty or whitespace-only/empty-HTML (e.g. Quill "<p><br></p>") as empty
    const stripped = (editorContent || "").replace(/<[^>]*>/g, "").trim();
    if (!stripped) {
      toast.error("Please enter follow-up remarks");
      return;
    }

    setIsSubmitting(true);

    try {
      const formData = new FormData();

      const entityIdKey = SOURCE_ENTITY_ID_MAP[activeType];
      if (!entityIdKey || !data?.[entityIdKey]) {
        throw new Error("Invalid base entity type or missing entity id");
      }

      // 🔹 REQUIRED payload (exact match)
      formData.append("source_entity_type", activeType);
      formData.append("source_entity_id", data?.[entityIdKey]);
      formData.append("follow_up_date", localDateTimeToUtcIso(followUpData.dateTime));
      formData.append(
        "next_follow_up_date",
        followUpData.nextFollowUpDate
          ? localDateToUtcIso(followUpData.nextFollowUpDate)
          : ""
      );
      formData.append("follow_up_type", followUpData.followUpType);
      formData.append("follow_up_remarks", editorContent.trim());
      if (category) {
        formData.append("follow_up_category", category);
      }

      const response = await dispatch(createFollowUpByType(formData)).unwrap();
      const newActivity = mapFollowUpToActivity(response.data);
      setActivities((prev) => [...prev, newActivity]);
      toast.success("Follow-up created successfully");

      // 🔹 Reset form
      setFollowUpData({
        followUpType: "",
        dateTime: getCurrentDateTime(),
        nextFollowUpDate: "",
        attachmentFiles: [],
      });
      setEditorContent("");
      // setEditingFollowUpId(null);
      setEditorResetKey((prev) => prev + 1);

      // Call onSuccess callback if provided
      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      console.error(error);
      toast.error(
        error?.response?.data?.message || "Failed to create follow-up"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Assigned Members from prop
  useEffect(() => {
    const allMembers: AssignedMember[] = [];
    const userIdsInAssignedMembers = new Set<string>();

    // First, process assigned members
    if (data?.assigned_members && Array.isArray(data.assigned_members)) {
      const uniqueMembers = Array.from(
        new Map(data.assigned_members.map((m: any) => [m.user_id, m])).values()
      );

      const mappedMembers = uniqueMembers.map(mapAssignedMember);
      allMembers.push(...mappedMembers);

      // Track which user IDs are already in assigned members
      uniqueMembers.forEach((m: any) => {
        if (m.user_id) {
          userIdsInAssignedMembers.add(m.user_id);
        }
      });
    }

    // Add "created by" user if available and not already in assigned members
    if (data?.created_by_user && data?.created_by) {
      const createdByUserId = data.created_by;

      // Only add if not already in assigned members
      if (!userIdsInAssignedMembers.has(createdByUserId)) {
        const createdByUser = data.created_by_user;
        const firstName = createdByUser.first_name || "";
        const lastName = createdByUser.last_name || "";
        const fullName = `${firstName} ${lastName}`.trim() || createdByUser.username || "Unknown User";
        const initials = `${firstName[0] || ""}${lastName[0] || ""}`.toUpperCase();

        // Deterministic color for created by user
        const colorIndex = initials.charCodeAt(0) % AVATAR_COLORS.length || 0;

        allMembers.unshift({
          id: `created_by_${createdByUserId}`,
          user: createdByUserId,
          username: fullName,
          initials,
          bg: AVATAR_COLORS[colorIndex].bg,
          color: AVATAR_COLORS[colorIndex].color,
          isCreatedBy: true,
        });
      } else {
        // If created by user is already in assigned members, mark the first occurrence
        const existingMember = allMembers.find(m => m.user === createdByUserId);
        if (existingMember) {
          existingMember.isCreatedBy = true;
        }
      }
    }

    console.log("MAPPED", allMembers);
    setAssignedMembers(allMembers);
  }, [data?.assigned_members, data?.created_by_user, data?.created_by]);

  // Fetch unified follow-up timeline
  useEffect(() => {
    const fetchFollowUp = async () => {
      setLoading(true);
      try {
        const requestPayload: {
          source_entity_type: string;
          source_entity_id: any;
          source_category?: string;
        } = {
          source_entity_type: activeType,
          source_entity_id: data?.[SOURCE_ENTITY_ID_MAP[activeType]],
        };

        if (category) {
          requestPayload.source_category = category;
        }

        const res = await dispatch(
          fetchFollowUpsByType(requestPayload)
        ).unwrap();

        const shouldExcludeFirstNoDate =
          category === SALES_ORDER_CATEGORY.RETENTION ||
          category === SALES_ORDER_CATEGORY.PBG;

        const followUpsRaw: any[] = Array.isArray(res?.data) ? res.data : [];
        const followUpsForFeed = shouldExcludeFirstNoDate
          ? excludeFirstNoDateFollowUp(followUpsRaw)
          : followUpsRaw;

        const activitiesMapped = followUpsForFeed.map(mapFollowUpToActivity);
        setActivities(activitiesMapped);
        console.log("RES for get api", res.data);
      } catch (error: any) {
        console.log("Error in followup modal", error);
        toast.error(error?.error || error?.message);
      } finally {
        setLoading(false);
      }
    };

    fetchFollowUp();
  }, [category, data, dispatch, activeType]);

  useEffect(() => {
    console.log("DATA from FOLLOW UP", data);
  }, [data]);

  return (
    <div
      style={{
        height: "100%",
        padding: "10px 32px",
        background: "linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)",
        borderRadius: "20px",
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
      }}
    >
      <Row className="g-4 h-100">
        {/* Left Column - Assigned Members & Schedule Follow-up */}
        <Col
          md={6}
          className="d-flex flex-column h-100"
          style={{
            paddingRight: "32px",
            borderRight: "2px solid #e8ecef",
          }}
        >
          {/* Scrollable Content Area */}
          <div
            className="flex-grow-1 overflow-auto"
            style={{
              paddingRight: "8px",
              marginRight: "-8px",
              paddingBottom: "20px",
            }}
          >
            {/* Assigned Members Section */}
            {showAssignedMembers && (assignedMembers && assignedMembers.length > 0) && (
              <div
                className="mb-3"
                style={{
                  padding: "20px",
                  backgroundColor: "#ffffff",
                  borderRadius: "12px",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
                }}
              >
                <div
                  className="fw-bold mb-3 d-flex align-items-center"
                  style={{
                    fontSize: "1.1rem",
                    color: "#2c3e50",
                    borderBottom: "2px solid #e8ecef",
                    paddingBottom: "12px",
                  }}
                >
                  <span
                    style={{
                      display: "inline-block",
                      width: "4px",
                      height: "20px",
                      backgroundColor: "#4a90e2",
                      borderRadius: "2px",
                      marginRight: "10px",
                    }}
                  />
                  Assigned Members
                </div>
                <AssignedMembers assignedMembers={assignedMembers || []} />
              </div>
            )}

            {/* Schedule Follow-up Section */}
            <div
              style={{
                padding: "20px",
                backgroundColor: "#ffffff",
                borderRadius: "12px",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
              }}
            >
              <div
                className="fw-bold mb-3 d-flex align-items-center"
                style={{
                  fontSize: "1.1rem",
                  color: "#2c3e50",
                  borderBottom: "2px solid #e8ecef",
                  paddingBottom: "12px",
                }}
              >
                <span
                  style={{
                    display: "inline-block",
                    width: "4px",
                    height: "20px",
                    backgroundColor: "#4a90e2",
                    borderRadius: "2px",
                    marginRight: "10px",
                  }}
                />
                Schedule Follow-up
              </div>
              <ScheduleFollowUp
                followUpData={followUpData}
                handleSubmitFollowUp={handleSubmitFollowUp}
                editorContent={editorContent}
                setEditorContent={setEditorContent}
                isSubmitting={isSubmitting}
                handleInputChange={handleInputChange}
                HARDCODED_FOLLOW_UP_TYPES={HARDCODED_FOLLOW_UP_TYPES}
                minFollowUpDateTime={minFollowUpDateTimeLocal}
                editorResetKey={editorResetKey}
                onCancel={onClose}
              />
            </div>
          </div>
        </Col>

        {/* Right Column - Activity Feed */}
        <Col md={6} className="d-flex flex-column h-100">
        <div
  className="mb-3 d-flex align-items-center justify-content-between"
  style={{
    padding: "12px 20px",
    backgroundColor: "#ffffff",
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
  }}
>
            <h5
              className="fw-bold mb-0 d-flex align-items-center"
              style={{
                fontSize: "1.1rem",
                color: "#2c3e50",
              }}
            >
              <span
                style={{
                  display: "inline-block",
                  width: "4px",
                  height: "20px",
                  backgroundColor: "#4a90e2",
                  borderRadius: "2px",
                  marginRight: "10px",
                }}
              />
              Comments and Activity
            </h5>
          </div>
          <div
            className="flex-grow-1 position-relative"
            style={{
              overflowY: "auto",
              minHeight: 0,
              padding: "20px",
              backgroundColor: "#ffffff",
              borderRadius: "12px",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
            }}
          >
            {loading ? (
              <div
                className="d-flex justify-content-center align-items-center"
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: "rgba(255, 255, 255, 0.9)",
                  borderRadius: "12px",
                  zIndex: 10,
                }}
              >
                <div className="text-center">
                  <Spinner
                    animation="border"
                    role="status"
                    style={{
                      width: "3rem",
                      height: "3rem",
                      borderWidth: "4px",
                      borderColor: "#ea6666",
                      borderRightColor: "transparent",
                    }}
                  >
                    <span className="visually-hidden">Loading activities...</span>
                  </Spinner>
                  <p
                    className="mt-3"
                    style={{
                      color: "#6c757d",
                      fontSize: "0.9rem",
                      fontWeight: 500,
                    }}
                  >
                    Loading activities...
                  </p>
                </div>
              </div>
            ) : (
              <ActivityFeed loading={loading} activities={activities} />
            )}
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default FollowupModel;

